import { useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import AppActionMenu from '../../../components/AppActionMenu'
import AppPagination from '../../../components/AppPagination'
import SharedFilters from '../../../components/SharedFilters'
import { appToast } from '../../../components/AppToast'
import { useExportJob } from '../../../hooks/useExportJob'
import { useBiometricRecordsListScreen } from './hooks/useBiometricRecordsListScreen'

function getInitials(name) {
  const clean = String(name || '').trim()
  if (!clean) return 'ST'
  const parts = clean.split(' ').filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return clean.slice(0, 2).toUpperCase()
}

function formatDateTime(dateTimeStr, timeStr) {
  if (!dateTimeStr) return '—'
  const d = new Date(dateTimeStr)
  if (Number.isNaN(d.getTime())) {
    return timeStr ? `${dateTimeStr} ${timeStr}` : dateTimeStr
  }
  const day = String(d.getDate()).padStart(2, '0')
  const month = d.toLocaleDateString('en-IN', { month: 'short' })
  const year = d.getFullYear()
  const displayTime = timeStr || d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })
  return `${day} ${month} ${year}, ${displayTime}`
}

export default function BiometricRecordsListScreen() {
  const navigate = useNavigate()
  const { state, actions } = useBiometricRecordsListScreen()

  useEffect(() => {
    if (state.error) appToast.error(state.error)
  }, [state.error])

  const toOptions = useCallback((arr) => {
    return (arr || []).map((item) =>
      typeof item === 'object' && item !== null
        ? item
        : { value: item, label: item }
    )
  }, [])

  const { triggerExport, isExporting } = useExportJob({ module: 'biometric' })

  const handleExportCSV = () => {
    triggerExport({
      searchField: state.searchField,
      searchText: state.searchText,
      company: state.companyFilter === 'All' ? '' : state.companyFilter,
      branch: state.branchFilter === 'All' ? '' : state.branchFilter,
      deviceId: state.deviceFilter === 'All' ? '' : state.deviceFilter,
      fromDate: state.fromDate,
      toDate: state.toDate,
      studentId: state.studentId,
    })
  }

  const fromEntry = state.totalCount === 0 ? 0 : (state.currentPage - 1) * state.pageSize + 1
  const toEntry = Math.min(state.currentPage * state.pageSize, state.totalCount)

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* ── Page Header (Shared ERP Theme) ─────────────────────────────────── */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">Admission Module</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Biometric Records
          </h1>
          <p className="mt-1 text-sm text-on-surface-variant">
            Live swipe card and biometric access logs synchronized with student profiles.
          </p>
        </div>

        <div className="flex items-center gap-3">
          {state.fromStudent && (
            <button
              type="button"
              onClick={actions.goBack}
              className="inline-flex items-center gap-2 rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-2 text-xs font-bold text-on-surface-variant transition-colors hover:bg-surface-container-high cursor-pointer"
            >
              <span className="material-symbols-outlined text-base">arrow_back</span>
              <span>Back to Students</span>
            </button>
          )}
          <span className="inline-flex items-center rounded-xl bg-primary/10 px-3.5 py-1.5 text-xs font-bold text-primary">
            {state.totalCount} {state.totalCount === 1 ? 'Record' : 'Records'}
          </span>
        </div>
      </section>

      {/* ── KPI Summary Cards ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {/* Total Swipes */}
        <div className="flex items-center gap-3.5 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-4 shadow-sm transition hover:shadow-md">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
            <span className="material-symbols-outlined text-xl">fingerprint</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Total Swipes</p>
            <p className="mt-0.5 text-lg font-extrabold tracking-tight text-blue-700 tabular-nums">
              {state.summary.totalSwipes}
            </p>
          </div>
        </div>

        {/* Unique Students */}
        <div className="flex items-center gap-3.5 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-4 shadow-sm transition hover:shadow-md">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-purple-600">
            <span className="material-symbols-outlined text-xl">groups</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Unique Students</p>
            <p className="mt-0.5 text-lg font-extrabold tracking-tight text-purple-700 tabular-nums">
              {state.summary.uniqueStudents}
            </p>
          </div>
        </div>

        {/* Active Devices */}
        <div className="flex items-center gap-3.5 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-4 shadow-sm transition hover:shadow-md">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
            <span className="material-symbols-outlined text-xl">router</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Active Devices</p>
            <p className="mt-0.5 text-lg font-extrabold tracking-tight text-amber-700 tabular-nums">
              {state.summary.activeDevices}
            </p>
          </div>
        </div>

        {/* Today's Swipes */}
        <div className="flex items-center gap-3.5 rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-4 shadow-sm transition hover:shadow-md">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
            <span className="material-symbols-outlined text-xl">punch_clock</span>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">Today's Swipes</p>
            <p className="mt-0.5 text-lg font-extrabold tracking-tight text-emerald-700 tabular-nums">
              {state.summary.todaySwipes}
            </p>
          </div>
        </div>
      </div>

      {/* ── Collapsible Advanced Filters (Shared Component) ────────────────── */}
      <SharedFilters
        searchField={state.searchField}
        searchText={state.searchText}
        setSearchField={actions.setSearchField}
        setSearchText={actions.setSearchText}
        searchFieldOptions={state.searchFieldOptions}
        showStatus={false}
        showSearchButtons={true}
        companyFilter={state.companyFilter}
        setCompanyFilter={actions.setCompanyFilter}
        companyOptions={toOptions(state.companyOptions)}
        branchFilter={state.branchFilter}
        setBranchFilter={actions.setBranchFilter}
        branchOptions={toOptions(state.branchOptions)}
        periodFilter={state.datePeriod}
        setPeriodFilter={actions.handleDatePeriodChange}
        periodOptions={state.datePeriodOptions}
        periodLabel="Period"
        fromDate={state.fromDate}
        setFromDate={actions.setFromDate}
        toDate={state.toDate}
        setToDate={actions.setToDate}
        onSearch={actions.search}
        onReset={actions.resetFilters}
        resetLabel="Show All"
        visibleAccordionFilters={[
          'company',
          'branch',
          'period',
          'fromDate',
          'toDate',
        ]}
        tools={[
          { key: 'print', label: 'Print', icon: 'print' },
          { key: 'export', label: isExporting ? 'Exporting...' : 'Download (Excel)', icon: 'download' },
        ]}
        onToolSelect={(key) => {
          if (key === 'export') handleExportCSV()
          else if (key === 'print') window.print()
        }}
      />

      {/* ── Data Table Card Container ───────────────────────────────────────── */}
      <section className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[950px] text-left text-sm">
            <thead>
              <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant">
                <th className="px-5 py-3.5">Date & Time</th>
                <th className="w-28 px-5 py-3.5">Roll / ID</th>
                <th className="px-5 py-3.5">Student Profile</th>
                <th className="w-32 px-5 py-3.5">Device</th>
                <th className="w-24 px-5 py-3.5 text-center">Status</th>
                <th className="px-5 py-3.5">Course / Batch</th>
                <th className="px-5 py-3.5">Branch / Company</th>
                <th className="px-5 py-3.5">Remark</th>
                <th className="w-16 px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-outline-variant/15">
              {state.isLoading ? (
                <tr>
                  <td colSpan={9} className="py-14 text-center text-sm font-medium text-on-surface-variant">
                    <div className="inline-flex items-center gap-2">
                      <span className="material-symbols-outlined animate-spin text-base text-primary">progress_activity</span>
                      <span>Loading biometric swipe card records...</span>
                    </div>
                  </td>
                </tr>
              ) : state.items.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-14 text-center text-sm font-medium text-on-surface-variant">
                    No Biometric swipe records found matching the specified criteria.
                  </td>
                </tr>
              ) : (
                state.items.map((row) => (
                  <tr
                    key={row.id}
                    className="transition-colors hover:bg-surface-container-low/40"
                  >
                    {/* Timestamp */}
                    <td className="px-5 py-3.5 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-sm text-on-surface-variant/60">
                          schedule
                        </span>
                        <span className="font-semibold text-on-surface tabular-nums">
                          {formatDateTime(row.dateTime || row.dateStr, row.time)}
                        </span>
                      </div>
                    </td>

                    {/* Roll No / ID Card No */}
                    <td className="px-5 py-3.5">
                      <span className="inline-flex items-center rounded-lg bg-surface-container-high px-2 py-0.5 font-mono text-xs font-bold text-on-surface">
                        {row.rollNo || row.idCardNo || row.grNo || '—'}
                      </span>
                    </td>

                    {/* Student Name */}
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-xs font-black text-primary">
                          {getInitials(row.studentName || row.fullName)}
                        </div>
                        <div>
                          <p className="font-bold text-on-surface">{row.studentName || row.fullName || 'Unassigned Student'}</p>
                          {row.studentCode ? (
                            <p className="text-[11px] font-mono text-on-surface-variant/80">
                              {row.studentCode}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </td>

                    {/* Device ID */}
                    <td className="px-5 py-3.5">
                      <span className="inline-flex items-center rounded-lg bg-surface-container-low px-2.5 py-1 font-mono text-xs font-semibold text-secondary">
                        {row.deviceId || '—'}
                      </span>
                    </td>

                    {/* Punch Status Badge */}
                    <td className="px-5 py-3.5 text-center">
                      <span
                        className={`inline-flex items-center justify-center rounded-lg px-2.5 py-0.5 text-[11px] font-extrabold uppercase tracking-wide ${
                          row.status === 'IN'
                            ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20'
                            : 'bg-zinc-100 text-zinc-700 ring-1 ring-zinc-500/20'
                        }`}
                      >
                        {row.status || 'PUNCH'}
                      </span>
                    </td>

                    {/* Course / Batch */}
                    <td className="px-5 py-3.5 text-xs font-medium text-on-surface-variant">
                      <p className="text-on-surface">{row.courseName || '—'}</p>
                      {row.batchNames ? (
                        <p className="text-[11px] text-on-surface-variant/70">{row.batchNames}</p>
                      ) : null}
                    </td>

                    {/* Branch / Company */}
                    <td className="px-5 py-3.5 text-xs font-medium text-on-surface-variant">
                      <p className="text-on-surface">{row.branch || '—'}</p>
                      {row.company ? (
                        <p className="text-[11px] text-on-surface-variant/70">{row.company}</p>
                      ) : null}
                    </td>

                    {/* Remark */}
                    <td className="px-5 py-3.5 text-xs text-on-surface-variant">
                      {row.remark || '—'}
                    </td>

                    {/* Actions */}
                    <td className="px-5 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                      <AppActionMenu
                        triggerLabel=""
                        triggerIcon="more_vert"
                        showChevron={false}
                        triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent cursor-pointer"
                        items={[
                          { key: 'view-student', label: 'View Student Profile', icon: 'person' },
                          { key: 'attendance', label: 'Daily Attendance', icon: 'calendar_today' },
                        ]}
                        onSelect={(key) => {
                          if (key === 'view-student') {
                            const q = row.studentName || row.fullName || row.rollNo || ''
                            navigate(`/admission/students/list?searchText=${encodeURIComponent(q)}`)
                          } else if (key === 'attendance') {
                            navigate('/attendance/daily')
                          }
                        }}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ── Table Footer & Shared Pagination ── */}
        <div className="flex flex-col items-center justify-between gap-4 border-t border-outline-variant/15 px-6 py-4 sm:flex-row">
          <p className="text-xs text-on-surface-variant font-medium">
            Showing <span className="font-bold text-on-surface">{fromEntry}</span> to{' '}
            <span className="font-bold text-on-surface">{toEntry}</span> of{' '}
            <span className="font-bold text-on-surface">{state.totalCount}</span> biometric punches
          </p>

          <AppPagination
            page={state.currentPage}
            totalPages={state.totalPages}
            onPageChange={actions.setCurrentPage}
            onPrevious={() => actions.setCurrentPage((p) => Math.max(1, p - 1))}
            onNext={() => actions.setCurrentPage((p) => Math.min(state.totalPages, p + 1))}
            canPrevious={state.currentPage > 1}
            canNext={state.currentPage < state.totalPages}
          />
        </div>
      </section>
    </div>
  )
}
